import React, { useEffect, useRef, useMemo } from 'react';
import moment from 'moment-timezone';

import constants from '../../constants';

const bridgeProductType = constants.BridgeProductType;

const getBucketMinuteOffset = () => moment.tz(moment.tz.guess()).utcOffset();

const AnalyticsHostElement = ({className, elementId, request, analyticsService, token, isUnity, analyticsApiUrl, productType}) => {
    const analyticsRef = useRef(null);

    useEffect(() => {
        analyticsRef.current.innerHTML = '';

        if (analyticsService && token) {
            analyticsService.injectChart(analyticsRef.current.id, request, analyticsApiUrl, token, productType, isUnity);
        }
    }, [request, analyticsService, token, isUnity, analyticsApiUrl, productType]);

    return <div className={className} id={elementId} ref={analyticsRef}></div>
};

export const AppInstalls = ({className, token, isUnity, analytics, startDate, endDate}) => {
    const request = useMemo(() => ({
        chartType: 'gaugearrow',
        queryObject:
            {
                "aggregate": 5,
                "fieldToAggregate": "DeviceId",
                "whereClause": [{
                    "fieldName": "EventName",
                    "operator": "==",
                    "fieldValue": "'ChurchSelection'"
                }, {
                    "fieldName": "EventType",
                    "operator": "==",
                    "fieldValue": "'Setup'"
                }],
                "timeSpan": {
                    "startDate": `${moment(startDate).format()}`,
                    "endDate": `${moment(endDate).format()}`
                },
                "includeChart": false,
                "bucketMinuteOffset": getBucketMinuteOffset()
            },
        showAggregateSummaryBar: true,
        showSummaryFooter: true,
        summaryFooterType: 'percent',
        titleText: 'App Installs'
    }), [startDate, endDate]);

    return <AnalyticsHostElement 
        className={className} 
        elementId='app-installs' 
        request={request} 
        analyticsService={analytics} 
        token={token} 
        isUnity={isUnity}
        analyticsApiUrl={window.ANALYTICS_API_URL} 
        productType={bridgeProductType} />;
};

export const ActiveUsers = ({className, token, isUnity, analytics, startDate, endDate}) => {
    const request = useMemo(() => ({
        chartType: 'gaugearrow',
        queryObject:
            {
                "bucketMinuteOffset": getBucketMinuteOffset(),
                "aggregate": 5,
                "fieldToAggregate": "DeviceId",
                "whereClause": [{
                    "fieldName": "EventName",
                    "operator": "==",
                    "fieldValue": "'GetConfig'"
                }, {
                    "fieldName": "EventType",
                    "operator": "==",
                    "fieldValue": "'Setup'"
                }],
                "timeSpan": {
                    "startDate": `${moment(startDate).format()}`,
                    "endDate": `${moment(endDate).format()}`
                },
                "includeChart": false,
                "includeCurrentSummary": true,
                "includePreviousSummary": true
            },
        showAggregateSummaryBar: true,
        showSummaryFooter: true,
        summaryFooterType: 'count',
        titleText: 'Active Users'
    }), [startDate, endDate]);

    return <AnalyticsHostElement 
        className={className} 
        elementId='active-users' 
        request={request} 
        analyticsService={analytics} 
        token={token} 
        isUnity={isUnity}
        analyticsApiUrl={window.ANALYTICS_API_URL} 
        productType={bridgeProductType} />;
};

export const Sessions = ({className, token, isUnity, analytics, startDate, endDate}) => {
    const request = useMemo(() => ({
        chartType: 'gaugearrow',
        queryObject:
            {
                "aggregate": 1,
                "whereClause": [{
                    "fieldName": "EventName",
                    "operator": "==",
                    "fieldValue": "'SessionStart'"
                }, {
                    "fieldName": "EventType",
                    "operator": "==",
                    "fieldValue": "'Setup'"
                }],
                "timeSpan": {
                    "startDate": `${moment(startDate).format()}`,
                    "endDate": `${moment(endDate).format()}`
                },
                "includeChart": false,
                "includeCurrentSummary": true,
                "includePreviousSummary": true,
                "bucketMinuteOffset": getBucketMinuteOffset()
            },
        showAggregateSummaryBar: true,
        showSummaryFooter: true,
        summaryFooterType: 'count',
        titleText: 'Sessions'
    }), [startDate, endDate]);

    return <AnalyticsHostElement 
        className={className} 
        elementId='sessions' 
        request={request} 
        analyticsService={analytics} 
        token={token} 
        isUnity={isUnity}
        analyticsApiUrl={window.ANALYTICS_API_URL} 
        productType={bridgeProductType} />;
};

export const LiveStreamViews = ({className, token, isUnity, analytics, startDate, endDate}) => {
    const request = useMemo(() => ({
        aggregateSuffixText: 'Total',
        chartType: 'timeline',
        queryObject: {
            "aggregate": 1,
            "whereClause": [{
                "fieldName": "EventName",
                "operator": "==",
                "fieldValue": "'Play'"
            }, {
                "fieldName": "EventType",
                "operator": "==",
                "fieldValue": "'LiveStream'"
            }],
            "timeSpan": {
                "startDate": `${moment(startDate).format()}`,
                "endDate": `${moment(endDate).format()}`
            },
            "bucketMinuteOffset": getBucketMinuteOffset()
        },
        showAggregateSummaryBar: true,
        showDateDropdown: true,
        tooltipSuffix: 'Views',
        titleText: 'Live Stream Views'
    }), [startDate, endDate]);

    return <AnalyticsHostElement 
        className={className} 
        elementId='live-stream-views' 
        request={request} 
        analyticsService={analytics} 
        token={token} 
        isUnity={isUnity}
        analyticsApiUrl={window.ANALYTICS_API_URL} 
        productType={bridgeProductType} />;
};

export const PageViews = ({className, token, isUnity, analytics, startDate, endDate}) => {
    const request = useMemo(() => ({
        chartType: 'categorybar',
        queryObject: {
            "aggregate": 1,
            "whereClause": [{
                "fieldName": "EventType",
                "operator": "==",
                "fieldValue": "'PageView'"
            }],
            "timeSpan": {
                "startDate": `${moment(startDate).format()}`,
                "endDate": `${moment(endDate).format()}`
            },
            "groupByFields": ["EventName"],
            "orderByFields": [
                "aggregateValue desc",
                "EventName asc"
            ],
            "subqueryField": "EventName",
            "subqueryValue": "Messages",
            "subqueryOrderByFields": [],
            "isSubquery": false,
            "whitelistedValues": ["Giving", "Messages", "Events", "Sermons", "Contact", "Directory", "Discover", "GivingHistory", "PrayerRequest", "SpecialDonations", "MyGroups", "GroupFinder"],
            "maxResultCount": 12,
            "bucketMinuteOffset": getBucketMinuteOffset()
        },
        showCategoryDropdown: true,
        showDateDropdown: true,
        tooltipSuffix: 'Views',
        titleText: 'Page Views',
        overrideLabels: [{text: "Giving History", value: "GivingHistory"}, {
            text: "Prayer Request",
            value: "PrayerRequest"
        }, {text: "Special Donations", value: "SpecialDonations"}, {
            text: "My Groups",
            value: "MyGroups"
        }, {text: "Group Finder", value: "GroupFinder"}]
    }), [startDate, endDate]);

    return <AnalyticsHostElement 
        className={className} 
        elementId='page-views' 
        request={request} 
        analyticsService={analytics} 
        token={token} 
        isUnity={isUnity}
        analyticsApiUrl={window.ANALYTICS_API_URL} 
        productType={bridgeProductType} />;
};

export const EventRegistrations = ({className, token, isUnity, analytics, startDate, endDate}) => {
    const request = useMemo(() => ({
        aggregateSuffixText: 'Total',
        chartType: 'categorybar',
        maxAxisTextLength: 12,
        queryObject: {

            "bucketMinuteOffset": getBucketMinuteOffset(),
            "aggregate": 1,
            "whereClause": [{
                "fieldName": "EventName",
                "operator": "==",
                "fieldValue": "'Submit'"
            }, {
                "fieldName": "EventType",
                "operator": "==",
                "fieldValue": "'EventForm'"
            }],
            "timeSpan": {
                "startDate": `${moment(startDate).format()}`,
                "endDate": `${moment(endDate).format()}`
            },
            "groupByFields": ["formId"],
            "orderByFields": [
                "aggregateValue desc"
            ],
            "maxResultCount": 10,
            "isLabelFMSForm": true
        },
        showAggregateSummaryBar: true,
        tooltipSuffix: 'Registrations',
        titleText: 'Most Active Event Registrations'
    }), [startDate, endDate]);

    return <AnalyticsHostElement 
        className={className} 
        elementId='event-registration' 
        request={request} 
        analyticsService={analytics} 
        token={token} 
        isUnity={isUnity}
        analyticsApiUrl={window.ANALYTICS_API_URL} 
        productType={bridgeProductType} />;
};

export const ContactFormSubmissions = ({className, token, isUnity, analytics, startDate, endDate}) => {
    const request = useMemo(() => ({
        aggregateSuffixText: 'Total',
        chartType: 'timeline',
        maxAxisTextLength: 12,
        queryObject: {
            "bucketMinuteOffset": getBucketMinuteOffset(),
            "aggregate": 1,
            "whereClause": [{
                "fieldName": "EventName",
                "operator": "==",
                "fieldValue": "'Submit'"
            }, {
                "fieldName": "EventType",
                "operator": "==",
                "fieldValue": "'ContactForm'"
            }],
            "timeSpan": {
                "startDate": `${moment(startDate).format()}`,
                "endDate": `${moment(endDate).format()}`
            },
            "includeCurrentSummary": true
        },
        showAggregateSummaryBar: true,
        showDateDropdown: true,
        tooltipSuffix: 'Submissions',
        titleText: 'Contact Form Submissions'
    }), [startDate, endDate]);

    return <AnalyticsHostElement 
        className={className} 
        elementId='contact-form-submissions' 
        request={request} 
        analyticsService={analytics} 
        token={token} 
        isUnity={isUnity}
        analyticsApiUrl={window.ANALYTICS_API_URL} 
        productType={bridgeProductType} />;
};

export const SermonPlays = ({className, token, isUnity, analytics, startDate, endDate}) => {
    const request = useMemo(() => ({
        aggregateSuffixText: 'Total',
        chartType: 'categorybar',
        maxAxisTextLength: 12,
        queryObject: {
            "bucketMinuteOffset": getBucketMinuteOffset(),
            "aggregate": 1,
            "whereClause": [{
                "fieldName": "EventName",
                "operator": "==",
                "fieldValue": "'Play'"
            }, {
                "fieldName": "EventType",
                "operator": "==",
                "fieldValue": "'Sermon'"
            }],
            "timeSpan": {
                "startDate": `${moment(startDate).format()}`,
                "endDate": `${moment(endDate).format()}`
            },
            "groupByFields": ["resourceName", "resourceDate"],
            "orderByFields": [
                "aggregateValue desc"
            ],
            "maxResultCount": 10
        },
        showAggregateSummaryBar: true,
        showOrderByDropdown: true,
        orderByDropdownValues: [{
            text: "Most Popular",
            value: "aggregateValue desc",
            selected: true
        }, {text: "Most Recent", value: "resourceDate desc"}],
        tooltipSuffix: 'Plays',
        titleText: 'Sermon Plays'
    }), [startDate, endDate]);

    return <AnalyticsHostElement 
        className={className} 
        elementId='sermon-plays' 
        request={request} 
        analyticsService={analytics} 
        token={token} 
        isUnity={isUnity}
        analyticsApiUrl={window.ANALYTICS_API_URL} 
        productType={bridgeProductType} />;
};

export const Checkins = ({className, token, isUnity, analytics, startDate, endDate}) => {
    const request = useMemo(() => ({
        chartType: 'gaugearrow',
        hideTitleBar: false,
        maxAxisTextLength: 12,
        queryObject: {
            "bucketMinuteOffset": getBucketMinuteOffset(),
            "aggregate": 1,
            "whereClause": [{
                "fieldName": "EventType",
                "operator": "==",
                "fieldValue": "'CheckIn'"
            }], "timeSpan": {
                "startDate": `${moment(startDate).format()}`,
                "endDate": `${moment(endDate).format()}`
            },
            "includeChart": false,
        },
        showAggregateSummaryBar: true,
        showSummaryFooter: true,
        summaryFooterType: 'percent',
        titleText: 'Weekly Check-ins From App'
    }), [startDate, endDate]);

    return <AnalyticsHostElement 
        className={className} 
        elementId='checkins' 
        request={request} 
        analyticsService={analytics} 
        token={token} 
        isUnity={isUnity}
        analyticsApiUrl={window.ANALYTICS_API_URL} 
        productType={bridgeProductType} />;
};
